import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TeacherAbsenceManager from './TeacherAbsenceManager';
import './TeacherSpaceSimple.css';

const TeacherSpaceNew = ({ setCurrentPage }) => {
  const [step, setStep] = useState('login');
  const [activeTab, setActiveTab] = useState('accueil');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [teacher, setTeacher] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [filters, setFilters] = useState({ academicYear: '', semester: 1 });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [preferences, setPreferences] = useState({
    preferredDays: [],
    preferredTimeSlots: [],
    maxHoursPerDay: 6,
    maxHoursPerWeek: 20,
    consecutiveHoursMax: 4,
    lunchBreakRequired: true,
    unavailableSlots: [],
    preferredCourses: []
  });
  const [tracks, setTracks] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [courseFilters, setCourseFilters] = useState({ 
    academicYear: '', 
    department: '', 
    semester: 1, 
    track: '' 
  });
  const [preferencesTab, setPreferencesTab] = useState('preferences');

  const apiUrl = import.meta.env.VITE_API_URL || '';
  const timeSlots = ['8h30-10h00', '10h10-11h40', '11h50-13h20', '13h30-15h00', '15h10-16h40', '16h50-18h20'];
  const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

  const menuItems = [
    { id: 'accueil', label: 'Accueil', icon: '🏠' },
    { id: 'emplois', label: 'Emplois', icon: '📅' },
    { id: 'absences', label: 'Absences', icon: '📋' },
    { id: 'preferences', label: 'Préférences', icon: '⚙️' },
    { id: 'rattrapages', label: 'Rattrapages/Absence', icon: '📝' },
    { 
      id: 'historiques', 
      label: 'Historiques', 
      icon: '📊',
      submenu: [
        { id: 'hist-absences', label: 'Absences', icon: '📋' },
        { id: 'hist-rattrapages', label: 'Rattrapages', icon: '📝' },
        { id: 'hist-notes', label: 'Notes d\'absences', icon: '📄' }
      ]
    }
  ];

  const sendCode = async () => {
    setLoading(true);
    setShowResend(false);
    try {
      const response = await axios.post(`${apiUrl}/api/teacher-auth/send-code`, { email });
      setStep('verify');
      setMessage('Code envoyé par email');
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.hasExistingCode) {
        setMessage(error.response.data.message);
        setShowResend(true);
      } else {
        setMessage(error.response?.data?.message || 'Erreur envoi code');
      }
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setLoading(true);
    try {
      await axios.post(`${apiUrl}/api/teacher-auth/resend-code`, { email });
      setStep('verify');
      setMessage('Code renvoyé par email');
      setShowResend(false);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Erreur renvoi code');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${apiUrl}/api/teacher-auth/verify-code`, { email, code });
      setTeacher(response.data.teacher);
      setStep('profile');
      loadData(response.data.teacher._id);
    } catch (error) {
      setMessage('Code invalide');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (teacherId) => {
    try {
      const [yearsRes, prefsRes, tracksRes, coursesRes] = await Promise.all([
        axios.get(`${apiUrl}/api/academic-years`),
        axios.get(`${apiUrl}/api/teacher-preferences/${teacherId}`),
        axios.get(`${apiUrl}/api/tracks`),
        axios.get(`${apiUrl}/api/courses`)
      ]);
      setAcademicYears(yearsRes.data);
      setPreferences(prefsRes.data.preferences || preferences);
      setTracks(tracksRes.data);
      setAllCourses(coursesRes.data);
      if (yearsRes.data.length > 0) {
        setFilters({ academicYear: yearsRes.data[0]._id, semester: 1 });
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
    }
  };

  const loadSchedule = async () => {
    if (!teacher?._id || !filters.academicYear) return;
    try {
      const params = new URLSearchParams();
      params.append('academicYear', filters.academicYear);
      params.append('semester', filters.semester);
      
      const response = await axios.get(`${apiUrl}/api/teacher-space/schedule/${teacher._id}?${params}`);
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Erreur emploi:', error);
    }
  };

  useEffect(() => {
    if (teacher && filters.academicYear) {
      loadSchedule();
    }
  }, [filters, teacher]);

  const getSessionsForSlot = (dayIndex, timeIndex) => {
    return sessions.filter(s => s.dayOfWeek === dayIndex + 1 && s.timeSlot === timeIndex + 1);
  };

  const updatePreferences = async (newPrefs) => {
    try {
      await axios.put(`${apiUrl}/api/teacher-preferences/${teacher._id}`, {
        preferences: newPrefs,
        preferredCourses: preferences.preferredCourses?.map(c => c._id) || []
      });
    } catch (error) {
      console.error('Erreur mise à jour préférences:', error);
    }
  };

  const handlePreferenceChange = (field, value) => {
    const newPrefs = { ...preferences, [field]: value };
    setPreferences(newPrefs);
    setTimeout(() => updatePreferences(newPrefs), 500);
  };

  const toggleDay = (dayIndex) => {
    const currentDays = preferences.preferredDays || [];
    const newDays = currentDays.includes(dayIndex + 1)
      ? currentDays.filter(d => d !== dayIndex + 1)
      : [...currentDays, dayIndex + 1];
    handlePreferenceChange('preferredDays', newDays);
  };

  const toggleTimeSlot = (slotIndex) => {
    const currentSlots = preferences.preferredTimeSlots || [];
    const newSlots = currentSlots.includes(slotIndex + 1)
      ? currentSlots.filter(s => s !== slotIndex + 1)
      : [...currentSlots, slotIndex + 1];
    handlePreferenceChange('preferredTimeSlots', newSlots);
  };

  const toggleUnavailableSlot = (day, timeSlot) => {
    const currentSlots = preferences.unavailableSlots || [];
    const exists = currentSlots.some(slot => slot.day === day && slot.timeSlot === timeSlot);
    
    const newSlots = exists
      ? currentSlots.filter(slot => !(slot.day === day && slot.timeSlot === timeSlot))
      : [...currentSlots, { day, timeSlot }];
    handlePreferenceChange('unavailableSlots', newSlots);
  };

  const isUnavailable = (day, timeSlot) => {
    const slots = preferences.unavailableSlots || [];
    return slots.some(slot => slot.day === day && slot.timeSlot === timeSlot);
  };

  const toggleCourseSelection = async (courseId) => {
    try {
      await axios.post(`${apiUrl}/api/teacher-preferences/${teacher._id}/toggle-course`, {
        courseId
      });
      // Recharger les données
      loadData(teacher._id);
    } catch (error) {
      console.error('Erreur toggle course:', error);
    }
  };

  const handleCourseFilterChange = (field, value) => {
    const newFilters = { ...courseFilters, [field]: value };
    if (field === 'department' || field === 'academicYear') {
      newFilters.track = '';
    }
    setCourseFilters(newFilters);
  };

  const getFilteredCourses = () => {
    if (!allCourses || allCourses.length === 0) return [];
    
    return allCourses.filter(course => {
      const matchesYear = !courseFilters.academicYear || 
        (course.academicYear && course.academicYear._id === courseFilters.academicYear);
      const matchesDept = !courseFilters.department || 
        (course.department && course.department._id === courseFilters.department);
      const matchesSemester = course.semester === courseFilters.semester;
      const matchesTrack = !courseFilters.track || 
        (course.track && course.track._id === courseFilters.track);
      
      return matchesYear && matchesDept && matchesSemester && matchesTrack;
    });
  };

  const getFilteredTracks = () => {
    if (!courseFilters.department || !courseFilters.academicYear) {
      return tracks;
    }
    
    const relevantTrackIds = new Set(
      allCourses
        .filter(course => 
          course.department && course.department._id === courseFilters.department &&
          course.academicYear && course.academicYear._id === courseFilters.academicYear &&
          course.semester === courseFilters.semester
        )
        .map(course => course.track && course.track._id)
        .filter(Boolean)
    );
    
    return tracks.filter(track => relevantTrackIds.has(track._id));
  };

  const getSelectedCourses = () => {
    return preferences.preferredCourses || [];
  };

  const getFilteredSelectedCourses = () => {
    const selectedCourses = getSelectedCourses();
    
    if (!courseFilters.academicYear) {
      return selectedCourses;
    }
    
    return selectedCourses.filter(course => {
      const matchesYear = course.academicYear === courseFilters.academicYear || 
        (course.academicYear && course.academicYear._id === courseFilters.academicYear);
      const matchesSemester = course.semester === courseFilters.semester;
      const matchesDept = !courseFilters.department || 
        course.department === courseFilters.department ||
        (course.department && course.department._id === courseFilters.department);
      const matchesTrack = !courseFilters.track || 
        course.track === courseFilters.track ||
        (course.track && course.track._id === courseFilters.track);
      
      return matchesYear && matchesSemester && matchesDept && matchesTrack;
    });
  };

  const renderPreferences = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold mb-4">Mes Préférences</h2>
        
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setPreferencesTab('preferences')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                preferencesTab === 'preferences'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Préférences Horaires
            </button>
            <button
              onClick={() => setPreferencesTab('courses')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                preferencesTab === 'courses'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Cours
            </button>
          </nav>
        </div>
      </div>
      
      <div className="p-6">
        {preferencesTab === 'preferences' && (
          <div className="space-y-6">
            <div>
              <h4 className="font-medium mb-3">Jours préférés</h4>
              <div className="grid grid-cols-3 gap-2">
                {days.map((day, index) => (
                  <button
                    key={index}
                    onClick={() => toggleDay(index)}
                    className={`p-2 text-sm rounded border ${
                      (preferences.preferredDays || []).includes(index + 1)
                        ? 'bg-blue-100 border-blue-300 text-blue-800'
                        : 'bg-gray-50 border-gray-300 text-gray-700'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-medium mb-3">Créneaux horaires préférés</h4>
              <div className="grid grid-cols-2 gap-2">
                {timeSlots.map((slot, index) => (
                  <button
                    key={index}
                    onClick={() => toggleTimeSlot(index)}
                    className={`p-2 text-sm rounded border ${
                      (preferences.preferredTimeSlots || []).includes(index + 1)
                        ? 'bg-blue-100 border-blue-300 text-blue-800'
                        : 'bg-gray-50 border-gray-300 text-gray-700'
                    }`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max heures/jour</label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={preferences.maxHoursPerDay || 6}
                  onChange={(e) => handlePreferenceChange('maxHoursPerDay', parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max heures/semaine</label>
                <input
                  type="number"
                  min="1"
                  max="40"
                  value={preferences.maxHoursPerWeek || 20}
                  onChange={(e) => handlePreferenceChange('maxHoursPerWeek', parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max heures consécutives</label>
                <input
                  type="number"
                  min="1"
                  max="6"
                  value={preferences.consecutiveHoursMax || 4}
                  onChange={(e) => handlePreferenceChange('consecutiveHoursMax', parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex items-center">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={preferences.lunchBreakRequired !== false}
                    onChange={(e) => handlePreferenceChange('lunchBreakRequired', e.target.checked)}
                    className="mr-2"
                  />
                  Pause déjeuner requise
                </label>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium mb-3">Créneaux indisponibles</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="text-left p-1">Horaire</th>
                      {days.map((day, dayIndex) => (
                        <th key={dayIndex} className="text-center p-1 min-w-16">{day.slice(0, 3)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((slot, slotIndex) => (
                      <tr key={slotIndex}>
                        <td className="p-1 font-medium text-xs">{slot}</td>
                        {days.map((day, dayIndex) => (
                          <td key={dayIndex} className="p-1 text-center">
                            <button
                              onClick={() => toggleUnavailableSlot(dayIndex + 1, slotIndex + 1)}
                              className={`w-6 h-6 rounded text-xs ${
                                isUnavailable(dayIndex + 1, slotIndex + 1)
                                  ? 'bg-red-200 text-red-800'
                                  : 'bg-green-100 text-green-800 hover:bg-green-200'
                              }`}
                            >
                              {isUnavailable(dayIndex + 1, slotIndex + 1) ? '❌' : '✅'}
                            </button>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        
        {preferencesTab === 'courses' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Année universitaire</label>
                <select 
                  value={courseFilters.academicYear} 
                  onChange={(e) => handleCourseFilterChange('academicYear', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                >
                  <option value="">Toutes les années</option>
                  {academicYears.map(year => (
                    <option key={year._id} value={year._id}>{year.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
                <select 
                  value={courseFilters.department} 
                  onChange={(e) => handleCourseFilterChange('department', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                >
                  <option value="">Tous les départements</option>
                  {teacher?.departments?.map(dept => (
                    <option key={dept._id} value={dept._id}>{dept.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
                <select 
                  value={courseFilters.semester} 
                  onChange={(e) => handleCourseFilterChange('semester', parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                >
                  <option value={1}>Semestre 1</option>
                  <option value={2}>Semestre 2</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parcours</label>
                <select 
                  value={courseFilters.track} 
                  onChange={(e) => handleCourseFilterChange('track', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                >
                  <option value="">Tous les parcours</option>
                  {getFilteredTracks().map(track => (
                    <option key={track._id} value={track._id}>{track.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium mb-3">Matières sélectionnées ({getFilteredSelectedCourses().length})</h4>
              {getFilteredSelectedCourses().length === 0 ? (
                <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                  <div className="text-3xl mb-2">📚</div>
                  <p>Aucune matière sélectionnée</p>
                </div>
              ) : (
                <div className="grid gap-2">
                  {getFilteredSelectedCourses().map(course => (
                    <div key={course._id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div>
                        <span className="font-medium">{course.name}</span>
                        <span className="text-sm text-gray-500 ml-2">({course.code})</span>
                      </div>
                      <button
                        onClick={() => toggleCourseSelection(course._id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h4 className="font-medium mb-3">Matières disponibles ({getFilteredCourses().length})</h4>
              {getFilteredCourses().length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📚</div>
                  <p>Aucune matière trouvée</p>
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {getFilteredCourses().map(course => (
                    <label key={course._id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={(preferences.preferredCourses || []).some(c => c._id === course._id)}
                        onChange={() => toggleCourseSelection(course._id)}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <span className="font-medium">{course.name}</span>
                        <span className="text-sm text-gray-500 ml-2">({course.code})</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (step !== 'profile') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-center mb-6">Espace Enseignant</h1>
          
          {step === 'login' && (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  placeholder="Votre email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              <button
                onClick={sendCode}
                disabled={loading || !email}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 mb-3"
              >
                {loading ? 'Envoi...' : 'Envoyer le code'}
              </button>
              
              {showResend && (
                <button
                  onClick={resendCode}
                  disabled={loading}
                  className="w-full bg-orange-600 text-white py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50 mb-3"
                >
                  {loading ? 'Envoi...' : 'Renvoyer le code'}
                </button>
              )}
              
              <button
                onClick={() => setStep('verify')}
                className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
              >
                J'ai un code
              </button>
            </div>
          )}

          {step === 'verify' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Code envoyé à {email}</p>
              <input
                type="text"
                placeholder="Code à 6 caractères"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border rounded-lg"
                maxLength={6}
              />
              <button
                onClick={verifyCode}
                disabled={loading || code.length !== 6}
                className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Vérification...' : 'Vérifier'}
              </button>
              <button
                onClick={() => setStep('login')}
                className="w-full text-gray-600 hover:text-gray-800"
              >
                Changer d'email
              </button>
            </div>
          )}

          {message && (
            <div className={`mt-4 p-3 rounded text-sm ${
              message.includes('Erreur') ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
            }`}>
              {message}
            </div>
          )}

          <button
            onClick={() => setCurrentPage('home')}
            className="w-full mt-4 text-gray-500 hover:text-gray-700"
          >
            ← Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'accueil':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold mb-4">Bienvenue {teacher?.firstName} {teacher?.lastName}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl mb-2">📅</div>
                  <h3 className="font-semibold">Emploi du temps</h3>
                  <p className="text-sm text-gray-600">Consultez votre planning</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl mb-2">📋</div>
                  <h3 className="font-semibold">Absences</h3>
                  <p className="text-sm text-gray-600">Gérez les présences</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-2xl mb-2">📊</div>
                  <h3 className="font-semibold">Historiques</h3>
                  <p className="text-sm text-gray-600">Consultez l'historique</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'emplois':
        return (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h2 className="text-xl font-semibold">Mon Emploi du Temps</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select 
                    value={filters.academicYear} 
                    onChange={(e) => setFilters({...filters, academicYear: e.target.value})}
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="">Sélectionner une année</option>
                    {academicYears.map(year => (
                      <option key={year._id} value={year._id}>{year.name}</option>
                    ))}
                  </select>
                  
                  <select 
                    value={filters.semester} 
                    onChange={(e) => setFilters({...filters, semester: parseInt(e.target.value)})}
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value={1}>Semestre 1</option>
                    <option value={2}>Semestre 2</option>
                  </select>
                </div>
              </div>
            </div>
            
            {filters.academicYear ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horaires</th>
                      {days.map(day => (
                        <th key={day} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{day}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {timeSlots.map((slot, timeIndex) => (
                      <tr key={timeIndex}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-xs">
                            <div className="font-medium">{timeIndex + 1}</div>
                            <div className="text-gray-500">{slot}</div>
                          </div>
                        </td>
                        {days.map((day, dayIndex) => {
                          const sessionsForSlot = getSessionsForSlot(dayIndex, timeIndex);
                          return (
                            <td key={dayIndex} className="px-4 py-4 align-top">
                              {sessionsForSlot.map(session => (
                                <div key={session._id} className="p-2 mb-1 bg-blue-50 rounded text-xs">
                                  <div className="font-medium text-blue-900">{session.course?.name}</div>
                                  <div className="text-blue-700">{session.class?.name}</div>
                                  <div className="text-blue-600">{session.room?.name}</div>
                                </div>
                              ))}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">📅</div>
                <p className="text-gray-600">Sélectionnez une année pour voir l'emploi du temps</p>
              </div>
            )}
          </div>
        );

      case 'absences':
        return <TeacherAbsenceManager teacher={teacher} />;

      case 'preferences':
        return renderPreferences();

      default:
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">{menuItems.find(item => item.id === activeTab)?.label}</h2>
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🚧</div>
              <p className="text-gray-600">Fonctionnalité en développement</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <h1 className="text-lg font-semibold">Espace Enseignant</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        
        <nav className="mt-4 px-4">
          {menuItems.map(item => (
            <div key={item.id}>
              <button
                onClick={() => {
                  setActiveTab(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md mb-1 ${
                  activeTab === item.id || activeTab.startsWith(item.id)
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                {item.label}
              </button>
              
              {item.submenu && (activeTab === item.id || activeTab.startsWith('hist-')) && (
                <div className="ml-6 space-y-1">
                  {item.submenu.map(subItem => (
                    <button
                      key={subItem.id}
                      onClick={() => {
                        setActiveTab(subItem.id);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-md ${
                        activeTab === subItem.id
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="mr-2">{subItem.icon}</span>
                      {subItem.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        
        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={() => setCurrentPage('home')}
            className="w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border rounded-md"
          >
            Déconnexion
          </button>
        </div>
      </div>

      {/* Overlay pour mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-0">
        {/* Header mobile */}
        <div className="lg:hidden bg-white shadow-sm border-b px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900"
          >
            ☰
          </button>
          <h1 className="text-lg font-semibold">{teacher?.firstName} {teacher?.lastName}</h1>
          <div className="w-10"></div>
        </div>

        {/* Content */}
        <div className="p-4 lg:p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default TeacherSpaceNew;